import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NoImageIcon from '../../assets/Svg/NoImageIcon';
import FormTextField from '../../components/FormTextField';
import { ImageUpload } from '../../components/ImageUpload';
import { FixedTab, RouteTabs } from '../../components/RouteTabs';
import TableSkeleton from '../../components/TableSkeleton';
import { useWriter, useWriterMutation, useWriterStories, useWriterTransferLinks, useWriterContracts } from '../../hooks/queries/useWriter';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import settings from '../../services/settings';
import { IMinimumGuarantee, ISeason, IWriter, TransferContractDealType } from '../../services/types';
import { numberToCurrency, numberToPercentage } from '../../services/utils';
import { RouteNames } from '../config';

interface IProps {
  writerId?: number;
  onUpdate?: (writer: IWriter) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 16,
    paddingBottom: 16,
  },

  uploaderWrap: {
    padding: 0,
    width: 'fit-content',
  },

  imageWrap: {
    width: 200,
    height: 200,
    borderRadius: 6,
    borderWidth: 0,
    cursor: 'pointer',
    margin: 0,
  },

  imageDescription: {
    display: 'none',
  },

  imageCaption: {
    fontSize: 12,
    textAlign: 'center',
    color: theme.palette.text.primary,
    opacity: 0.38,
    marginTop: 10,
  },

  textFieldMargin: {
    marginBottom: 32,
  },

  checkboxLabel: {
    fontSize: 14,
    letterSpacing: 'normal',
    color: '#666666',
  },

  idTableCell: {
    width: 106,
  },

  createdAtTableCell: {
    width: 220,
  },

  tableRow: {
    height: 52,
  },

  tabs: {
    paddingTop: 24,
  },

  subPaper: {
    marginTop: 4,
  },

  actionButtonCell: {
    width: '180px',
  },

  actionButton: {
    width: '160px',
    height: '36px',
    borderRadius: '18px',
    backgroundColor: `${theme.palette.primary.main}18`,
    color: theme.palette.primary.main,
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: 'normal',
  },
}));

const PermissionChangeDialogTitle = {
  title: 'Change of Permissions',
  text: "Are you sure you want to change this writer's permission?",
};

const WriterAccountView = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const path = RouteNames.WriterAccount;

  const { writerId: currentWriterId } = props;

  const [writer, setWriter] = useState<IWriter | Partial<IWriter>>({});
  const parsedJoinedAt = moment(writer.joinedAt);

  const confirm = useConfirmDialog();

  const { data: writerFromQuery, isLoading: isWriterLoading } = useWriter(currentWriterId);
  const { update, removeEditorFromStory } = useWriterMutation();

  const { data: transferLinksFromQuery, isLoading: isTransferLinksLoading } = useWriterTransferLinks(currentWriterId);
  const transferLinks = transferLinksFromQuery;

  const { data: storiesFromQuery, isLoading: isStoriesLoading } = useWriterStories(currentWriterId);
  const editableStories = storiesFromQuery?.items;

  const { data: contractsFromQuery, isLoading: isContractsLoading } = useWriterContracts(currentWriterId);
  const editableContracts = contractsFromQuery;

  useEffect(() => {
    if (writerFromQuery) {
      setWriter(writerFromQuery);
    }
  }, [writerFromQuery]);

  const handleCanCreatePennamesChange = () => {
    confirm.open({
      ...PermissionChangeDialogTitle,
      onProceed: async () => {
        const currentCanCreatePennames = !!writer.permission?.canCreatePennames;
        const result = await update.mutateAsync({ ...writer.permission, canCreatePennames: !currentCanCreatePennames });
        setWriter(ps => ({ ...ps, ...result }));
      },
    });
  };

  const handleCanGrantPaymentAccountChange = () => {
    confirm.open({
      ...PermissionChangeDialogTitle,
      onProceed: async () => {
        const currentCanGrantPaymentAccount = !!writer.permission?.canGrantPaymentAccount;
        const result = await update.mutateAsync({ ...writer.permission, canGrantPaymentAccount: !currentCanGrantPaymentAccount });
        setWriter(ps => ({ ...ps, ...result }));
      },
    });
  };

  const handleCanGrantStoryEditorChange = () => {
    confirm.open({
      ...PermissionChangeDialogTitle,
      onProceed: async () => {
        const currentCanGrantStoryEditor = !!writer.permission?.canGrantStoryEditor;
        const result = await update.mutateAsync({ ...writer.permission, canGrantStoryEditor: !currentCanGrantStoryEditor });
        setWriter(ps => ({ ...ps, ...result }));
      },
    });
  };

  const handleDeleteEditPermissionClick = (storyId: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    confirm.open({
      title: 'Delete edit permission',
      text: "Are you sure you want to delete this writer's edit permission?",
      onProceed: () => {
        if (!writer.id) {
          return;
        }

        removeEditorFromStory.mutate({ storyId, editorId: writer.id });
      },
    });
  };

  const handleChangeLinkClick =
    ({ id, storyId }: ISeason) =>
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      window.open(`${settings.getCrewURI()}/stories/${storyId}/seasons/${id}`, '_blank', 'noopener noreferrer');
    };

  const renderAdvancePayment = (dealType: TransferContractDealType, minimumGuarantee: IMinimumGuarantee): string => {
    if (dealType !== TransferContractDealType.MinimumGuarantee || !minimumGuarantee) {
      return '-';
    }
    const { advancePayment, shareDuring } = minimumGuarantee;
    return `${numberToCurrency(advancePayment)}(${numberToPercentage(shareDuring)})`;
  };

  const renderDealAmount = (dealType: TransferContractDealType, minimumGuarantee: IMinimumGuarantee, value: number | string) => {
    if (dealType === TransferContractDealType.FlatFee) {
      return `${numberToCurrency(value)}`;
    } else if (dealType === TransferContractDealType.MinimumGuarantee) {
      return `${numberToPercentage(minimumGuarantee?.shareAfter)}`;
    } else {
      return `${numberToPercentage(value)}`;
    }
  };

  return (
    <>
      <Paper className={classes.wrap}>
        <Grid container>
          <Grid item>
            <Box width={200}>
              <ImageUpload
                onFileLoaded={() => {}}
                src={writer.photoUrl}
                readonly={true}
                placeholderRenderer={() => <NoImageIcon />}
                className={classes.uploaderWrap}
                imageWrapClassName={classes.imageWrap}
                descriptionClassName={classes.imageDescription}
              />
              <Typography className={classes.imageCaption}>Display image</Typography>
            </Box>
          </Grid>

          <Grid item>
            <Box display="flex" flexDirection="column" paddingLeft={'24px'} paddingRight={'24px'} width={'400px'} boxSizing="content-box">
              {currentWriterId && (
                <FormTextField
                  defaultValue={String(currentWriterId)}
                  name="User ID"
                  label="User ID"
                  isEditing={false}
                  isLoading={isWriterLoading}
                  disabled={true}
                  fullWidth
                  className={classes.textFieldMargin}
                />
              )}
              <FormTextField
                defaultValue={writer.username}
                name="Username"
                label="Username"
                placeholder="Add Writer's name"
                isEditing={false}
                isLoading={isWriterLoading}
                disabled={true}
                fullWidth
                className={classes.textFieldMargin}
              />
              <FormTextField
                defaultValue={writer.email}
                name="Email"
                label="Writer's Email"
                placeholder="Add Writer's email"
                isEditing={false}
                isLoading={isWriterLoading}
                disabled={true}
                fullWidth
                className={classes.textFieldMargin}
              />
              <FormTextField
                defaultValue={`${parsedJoinedAt.fromNow()} (${parsedJoinedAt.format('LLLL')})`}
                name="Joined at"
                label="Joined at"
                isEditing={false}
                isLoading={isWriterLoading}
                disabled={true}
                fullWidth
                className={classes.textFieldMargin}
              />
            </Box>
          </Grid>

          <Grid item>
            <Box display="flex" paddingBottom="32px" height="100%" flexDirection="column" justifyContent="flex-end">
              <Typography>Permission</Typography>
              <FormControlLabel
                control={
                  <Checkbox checked={writer.permission?.canCreatePennames || false} onChange={handleCanCreatePennamesChange} color="primary" />
                }
                label="Can create multiple pen names"
                classes={{
                  label: classes.checkboxLabel,
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={writer.permission?.canGrantPaymentAccount || false}
                    onChange={handleCanGrantPaymentAccountChange}
                    color="primary"
                  />
                }
                label="Can link stories to payment account"
                classes={{
                  label: classes.checkboxLabel,
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={writer.permission?.canGrantStoryEditor || false} onChange={handleCanGrantStoryEditorChange} color="primary" />
                }
                label="Can assign editable stories"
                classes={{
                  label: classes.checkboxLabel,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <RouteTabs path={`${path}/v/${currentWriterId}`} isNested={true} minWidth={200} className={classes.tabs}>
        <FixedTab label="Pen Names">
          {/* Pen Names */}
          <Paper className={classes.subPaper}>
            <Box padding={'20px 20px 10px'}>
              <Typography variant="h6">Pen Names</Typography>
            </Box>
            <Table>
              <TableHead>
                <TableCell className={classes.idTableCell}>Pen Name ID</TableCell>
                <TableCell>Pen Names</TableCell>
                <TableCell>Stories</TableCell>
                <TableCell>Other Assigned Accounts</TableCell>
                <TableCell className={classes.createdAtTableCell}>Created At</TableCell>
              </TableHead>
              <TableBody>
                {isWriterLoading && <TableSkeleton rows={5} columns={5} />}
                {!isWriterLoading &&
                  writer.licensedPennames &&
                  writer.licensedPennames.map(penname => {
                    const { id, name, licensees, createdAt, storyCount } = penname;
                    return (
                      <TableRow key={id} component={Link} to={`${RouteNames.Penname}/v/${id}`} hover className={classes.tableRow}>
                        <TableCell>{id}</TableCell>
                        <TableCell>{name}</TableCell>
                        <TableCell>{storyCount}</TableCell>
                        <TableCell>{licensees && licensees.map(({ username }) => username).join(', ')}</TableCell>
                        <TableCell>{moment(createdAt).format('LLL')}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            {!isWriterLoading && isEmpty(writer.licensedPennames) && (
              <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
                <Typography variant="body1">There are no items to show.</Typography>
              </Box>
            )}
          </Paper>
        </FixedTab>

        <FixedTab label="Payment Stories (Seasons)" uri="payment">
          <Typography variant="h1"></Typography>
          <Paper className={classes.subPaper}>
            <Box padding={'20px 20px 10px'}>
              <Typography variant="h6">Payment Stories (Seasons)</Typography>
            </Box>
            <Table>
              <TableHead>
                <TableCell className={classes.idTableCell}>Story Id</TableCell>
                <TableCell>Story Title</TableCell>
                <TableCell>Season Title</TableCell>
                <TableCell className={classes.createdAtTableCell}>Linked At</TableCell>
                <TableCell className={classes.createdAtTableCell}>Created At</TableCell>
                <TableCell className={classes.actionButtonCell} />
              </TableHead>
              <TableBody>
                {isTransferLinksLoading && <TableSkeleton rows={6} columns={5} />}
                {!isTransferLinksLoading &&
                  transferLinks &&
                  transferLinks.map(link => {
                    const { id, season, applyMonth, applyDay, createdAt } = link;
                    return (
                      <TableRow key={id} className={classes.tableRow}>
                        <TableCell>{season.storyId}</TableCell>
                        <TableCell>{season.story.title}</TableCell>
                        <TableCell>{season.title || `Season ${season.seasonNo}`}</TableCell>
                        <TableCell>{moment(`${applyMonth}${applyDay}`, 'YYYYMMDD').format('YYYY-MM-DD')}</TableCell>
                        <TableCell>{moment(createdAt).format('LLL')}</TableCell>
                        <TableCell>
                          <Button onClick={handleChangeLinkClick(season)} className={classes.actionButton}>
                            Change Link
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            {!isTransferLinksLoading && isEmpty(transferLinks) && (
              <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
                <Typography variant="body1">There are no items to show.</Typography>
              </Box>
            )}
          </Paper>
        </FixedTab>

        <FixedTab label="Editable Stories" uri="editable">
          {/* Editable Stories */}
          <Paper className={classes.subPaper}>
            <Box padding={'20px 20px 10px'}>
              <Typography variant="h6">Editable Stories</Typography>
            </Box>
            <Table>
              <TableHead>
                <TableCell className={classes.idTableCell}>Story Id</TableCell>
                <TableCell>Story Title</TableCell>
                <TableCell className={classes.createdAtTableCell}>Created At</TableCell>
                <TableCell className={classes.actionButtonCell} />
              </TableHead>
              <TableBody>
                {isStoriesLoading && <TableSkeleton rows={5} columns={4} />}
                {!isStoriesLoading &&
                  editableStories &&
                  editableStories.map(story => {
                    const { id, title, createdAt } = story;
                    return (
                      <TableRow
                        key={id}
                        component={'a'}
                        target="_blank"
                        href={`${settings.getCrewURI()}/stories/${id}`}
                        hover
                        className={classes.tableRow}
                      >
                        <TableCell>{id}</TableCell>
                        <TableCell>{title}</TableCell>
                        <TableCell>{moment(createdAt).format('LLL')}</TableCell>
                        <TableCell>
                          <Button onClick={handleDeleteEditPermissionClick(id)} className={classes.actionButton}>
                            Delete Edit Permission
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            {!isStoriesLoading && isEmpty(editableStories) && (
              <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
                <Typography variant="body1">There are no items to show.</Typography>
              </Box>
            )}
          </Paper>
        </FixedTab>

        <FixedTab label="Contract History" uri="contract-history">
          <Paper className={classes.subPaper}>
            <Box padding={'20px 20px 10px'}>
              <Typography variant="h6">Contract History</Typography>
            </Box>
            <Table>
              <TableHead>
                <TableCell>Contract Type</TableCell>
                <TableCell className={classes.createdAtTableCell}>
                  (License) <br />
                  Effective Date
                </TableCell>
                <TableCell className={classes.createdAtTableCell}>
                  (License) <br />
                  End Date
                </TableCell>
                <TableCell>Deal Type</TableCell>
                <TableCell>Advance Payment</TableCell>
                <TableCell>Deal Amount</TableCell>
              </TableHead>
              <TableBody>
                {isContractsLoading && <TableSkeleton rows={5} columns={4} />}
                {!isContractsLoading &&
                  editableContracts &&
                  editableContracts.map(contract => {
                    const { id, seasonId, writerId, startsAt, endsAt, dealType, value, minimumGuarantee } = contract;
                    return (
                      <TableRow key={id} component={Link} to={`${RouteNames.Payment}/v/${id}`} hover target="_blank" className={classes.tableRow}>
                        <TableCell>{writerId ? 'Writer' : seasonId ? 'Season' : 'Story'}</TableCell>
                        <TableCell>{moment(startsAt).format('LLL')}</TableCell>
                        <TableCell>{moment(endsAt).format('LLL')}</TableCell>
                        <TableCell>{dealType}</TableCell>
                        <TableCell>{renderAdvancePayment(dealType, minimumGuarantee as IMinimumGuarantee)}</TableCell>
                        <TableCell>{renderDealAmount(dealType, minimumGuarantee as IMinimumGuarantee, value)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            {!isContractsLoading && isEmpty(editableContracts) && (
              <Box width="100%" height="156px" display="flex" alignItems="center" justifyContent="center">
                <Typography variant="body1">There are no items to show.</Typography>
              </Box>
            )}
          </Paper>
        </FixedTab>
      </RouteTabs>
    </>
  );
};

export default WriterAccountView;
